package ru.villawonka.app.presentation.design.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.ButtonDefaults
import androidx.compose.material.Colors
import androidx.compose.material.Icon
import androidx.compose.material.MaterialTheme
import androidx.compose.material.OutlinedButton
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.ui.tooling.preview.Preview
import ru.villawonka.app.presentation.design.extensions.AppSpacer
import ru.villawonka.app.presentation.design.theme.AppColors
import ru.villawonka.app.presentation.design.theme.AppTextStyles
import ru.villawonka.app.presentation.design.theme.AppTheme
import villawonka.composeapp.generated.resources.Res
import villawonka.composeapp.generated.resources.ic_plus_24

@Composable
fun AppOutlinedButton(
    modifier: Modifier = Modifier,
    text: String,
    badge: String? = null,
    icon: (@Composable () -> Unit)? = null,
    enabled: Boolean = true,
    type: AppButtonType,
    size: AppButtonSize,
    onClick: () -> Unit,
) {
    val contentPadding = when (size) {
        AppButtonSize.LARGE ->
            if (icon == null) {
                PaddingValues(24.dp)
            } else {
                PaddingValues(
                    start = 16.dp,
                    end = 24.dp,
                    top = 24.dp,
                    bottom = 24.dp
                )
            }
        AppButtonSize.MEDIUM ->
            if (icon == null) {
                PaddingValues(
                    horizontal = 24.dp,
                    vertical = 12.dp
                )
            } else {
                PaddingValues(
                    start = 16.dp,
                    end = 24.dp,
                    top = 12.dp,
                    bottom = 12.dp
                )
            }
    }
    val borderColor = when (type) {
        AppButtonType.PRIMARY -> {
            if (enabled) {
                MaterialTheme.colors.outlinedButtonPrimaryOutlineEnabled
            } else {
                MaterialTheme.colors.outlinedButtonPrimaryOutlineDisabled
            }
        }
        AppButtonType.SECONDARY -> {
            if (enabled) {
                MaterialTheme.colors.outlinedButtonSecondaryOutlineEnabled
            } else {
                MaterialTheme.colors.outlinedButtonSecondaryOutlineDisabled
            }
        }
        AppButtonType.TERTIARY -> {
            if (enabled) {
                MaterialTheme.colors.outlinedButtonTertiaryOutlineEnabled
            } else {
                MaterialTheme.colors.outlinedButtonTertiaryOutlineDisabled
            }
        }
    }
    val colors = when (type) {
        AppButtonType.PRIMARY -> ButtonDefaults.outlinedButtonColors(
            backgroundColor = AppColors.transparent,
            contentColor = MaterialTheme.colors.outlinedButtonPrimaryContentEnabled,
            disabledContentColor = MaterialTheme.colors.outlinedButtonPrimaryContentDisabled,
        )
        AppButtonType.SECONDARY -> ButtonDefaults.outlinedButtonColors(
            backgroundColor = AppColors.transparent,
            contentColor = MaterialTheme.colors.outlinedButtonSecondaryContentEnabled,
            disabledContentColor = MaterialTheme.colors.outlinedButtonSecondaryContentDisabled,
        )
        AppButtonType.TERTIARY -> ButtonDefaults.outlinedButtonColors(
            backgroundColor = AppColors.transparent,
            contentColor = MaterialTheme.colors.outlinedButtonTertiaryContentEnabled,
            disabledContentColor = MaterialTheme.colors.outlinedButtonTertiaryContentDisabled,
        )
    }

    Box {
        badge?.let {
            Box(
                modifier = Modifier
                    .background(
                        color = borderColor,
                        shape = RoundedCornerShape(
                            topEnd = 16.dp,
                            bottomStart = 16.dp
                        )
                    )
                    .padding(
                        vertical = 4.dp,
                        horizontal = 12.dp,
                    )
                    .align(Alignment.TopEnd)
            ) {
                Text(
                    text = it,
                    style = AppTextStyles.labelSmallBold,
                    color = MaterialTheme.colors.filledBadgeContentInverted,
                )
            }
        }
        OutlinedButton(
            onClick = onClick,
            enabled = enabled,
            shape = RoundedCornerShape(12.dp),
            contentPadding = contentPadding,
            border = BorderStroke(
                width = 2.dp,
                color = borderColor,
            ),
            elevation = ButtonDefaults.elevation(
                defaultElevation = 0.dp,
                pressedElevation = 0.dp,
                disabledElevation = 0.dp,
                hoveredElevation = 0.dp,
                focusedElevation = 0.dp
            ),
            colors = colors,
            modifier = modifier
        ) {
            icon?.let {
                it()
                AppSpacer(10.dp)
            }
            Text(
                text = text,
                style = AppTextStyles.labelLargeMedium
            )
        }
    }
}


/**
 * Primary Content
 */

val Colors.outlinedButtonPrimaryContentEnabled
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.outlinedButtonPrimaryContentHovered
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.outlinedButtonPrimaryContentFocused
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.outlinedButtonPrimaryContentSelected
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.outlinedButtonPrimaryContentDisabled
    get() = if (isLight) AppColors.grayLight400 else AppColors.grayDark400


/**
 * Primary Background
 */

val Colors.outlinedButtonPrimaryBackgroundHovered
    get() = if (isLight) AppColors.grayLight200 else AppColors.grayDark600

val Colors.outlinedButtonPrimaryBackgroundFocused
    get() = if (isLight) AppColors.grayLight300 else AppColors.grayDark700

val Colors.outlinedButtonPrimaryBackgroundSelected
    get() = if (isLight) AppColors.violet300 else AppColors.violet600

/**
 * Primary Outline
 */

val Colors.outlinedButtonPrimaryOutlineEnabled
    get() = if (isLight) AppColors.violet700 else AppColors.violet700

val Colors.outlinedButtonPrimaryOutlineHovered
    get() = if (isLight) AppColors.violet700 else AppColors.violet700

val Colors.outlinedButtonPrimaryOutlineFocused
    get() = if (isLight) AppColors.violet700 else AppColors.violet700

val Colors.outlinedButtonPrimaryOutlineSelected
    get() = if (isLight) AppColors.violet700 else AppColors.violet700

val Colors.outlinedButtonPrimaryOutlineDisabled
    get() = if (isLight) AppColors.grayLight200 else AppColors.grayDark700


/**
 * Secondary Content
 */

val Colors.outlinedButtonSecondaryContentEnabled
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.outlinedButtonSecondaryContentHovered
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.outlinedButtonSecondaryContentFocused
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.outlinedButtonSecondaryContentSelected
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.outlinedButtonSecondaryContentDisabled
    get() = if (isLight) AppColors.grayLight400 else AppColors.grayDark400

/**
 * Secondary Background
 */

val Colors.outlinedButtonSecondaryBackgroundHovered
    get() = if (isLight) AppColors.grayLight200 else AppColors.grayDark600

val Colors.outlinedButtonSecondaryBackgroundFocused
    get() = if (isLight) AppColors.grayLight300 else AppColors.grayDark700

val Colors.outlinedButtonSecondaryBackgroundSelected
    get() = if (isLight) AppColors.blue300 else AppColors.blue600

/**
 * Secondary Outline
 */

val Colors.outlinedButtonSecondaryOutlineEnabled
    get() = if (isLight) AppColors.blue500 else AppColors.blue500

val Colors.outlinedButtonSecondaryOutlineHovered
    get() = if (isLight) AppColors.blue500 else AppColors.blue500

val Colors.outlinedButtonSecondaryOutlineFocused
    get() = if (isLight) AppColors.blue500 else AppColors.blue500

val Colors.outlinedButtonSecondaryOutlineSelected
    get() = if (isLight) AppColors.blue500 else AppColors.blue500

val Colors.outlinedButtonSecondaryOutlineDisabled
    get() = if (isLight) AppColors.grayLight200 else AppColors.grayDark700

/**
 * Tertiary Content
 */

val Colors.outlinedButtonTertiaryContentEnabled
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.outlinedButtonTertiaryContentHovered
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.outlinedButtonTertiaryContentFocused
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.outlinedButtonTertiaryContentSelected
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.outlinedButtonTertiaryContentDisabled
    get() = if (isLight) AppColors.grayLight400 else AppColors.grayDark500

/**
 * Tertiary Background
 */

val Colors.outlinedButtonTertiaryBackgroundHovered
    get() = if (isLight) AppColors.grayLight100 else AppColors.grayDark700

val Colors.outlinedButtonTertiaryBackgroundFocused
    get() = if (isLight) AppColors.grayLight100 else AppColors.grayDark800

val Colors.outlinedButtonTertiaryBackgroundSelected
    get() = if (isLight) AppColors.grayLight400 else AppColors.grayDark500

/**
 * Tertiary Outline
 */

val Colors.outlinedButtonTertiaryOutlineEnabled
    get() = if (isLight) AppColors.grayLight300 else AppColors.grayDark600

val Colors.outlinedButtonTertiaryOutlineHovered
    get() = if (isLight) AppColors.grayLight300 else AppColors.grayDark600

val Colors.outlinedButtonTertiaryOutlineFocused
    get() = if (isLight) AppColors.grayLight300 else AppColors.grayDark600

val Colors.outlinedButtonTertiaryOutlineSelected
    get() = if (isLight) AppColors.grayLight300 else AppColors.grayDark600

val Colors.outlinedButtonTertiaryOutlineDisabled
    get() = if (isLight) AppColors.grayLight200 else AppColors.grayDark700


/**
 * Preview: Dark Theme
 */

@Preview
@Composable
private fun PreviewInDarkTheme() {
    Column {
        AppTheme(isDarkTheme = true) {
            Column(
                modifier = Modifier
                    .background(color = Color.Black)
                    .padding(16.dp)
            ) {
                AppOutlinedButton(
                    text = "Primary large",
                    type = AppButtonType.PRIMARY,
                    size = AppButtonSize.LARGE
                ) {}
                AppSpacer(12.dp)
                AppOutlinedButton(
                    text = "Primary medium",
                    type = AppButtonType.PRIMARY,
                    size = AppButtonSize.MEDIUM
                ) {}
                AppSpacer(12.dp)
                AppOutlinedButton(
                    text = "Secondary large",
                    type = AppButtonType.SECONDARY,
                    size = AppButtonSize.LARGE
                ) {}
                AppSpacer(12.dp)
                AppOutlinedButton(
                    text = "Secondary medium",
                    type = AppButtonType.SECONDARY,
                    size = AppButtonSize.MEDIUM
                ) {}
                AppSpacer(12.dp)
                AppOutlinedButton(
                    text = "Tertiary large",
                    type = AppButtonType.TERTIARY,
                    size = AppButtonSize.LARGE
                ) {}
                AppSpacer(12.dp)
                AppOutlinedButton(
                    text = "Tertiary medium",
                    type = AppButtonType.TERTIARY,
                    size = AppButtonSize.MEDIUM
                ) {}
            }
        }
    }
}

@Preview
@Composable
private fun PreviewInDarkThemeWithIcon() {
    AppTheme(isDarkTheme = true) {
        Column(
            modifier = Modifier
                .background(color = Color.Black)
                .padding(16.dp)
        ) {
            AppOutlinedButton(
                text = "Primary large",
                icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Primary medium",
                icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Secondary large",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Secondary medium",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Tertiary large",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Tertiary medium",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.MEDIUM
            ) {}
        }
    }
}

/**
 * Preview: Light Theme
 */

@Preview
@Composable
private fun PreviewInLightTheme() {
    AppTheme(isDarkTheme = false) {
        Column(
            modifier = Modifier
                .background(color = Color.White)
                .padding(16.dp)
        ) {
            AppOutlinedButton(
                text = "Primary large",
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Primary medium",
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Secondary large",
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Secondary medium",
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Tertiary large",
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Tertiary medium",
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.MEDIUM
            ) {}
        }
    }
}

@Preview
@Composable
private fun PreviewInLightThemeWithIcon() {
    AppTheme(isDarkTheme = false) {
        Column(
            modifier = Modifier
                .background(color = Color.White)
                .padding(16.dp)
        ) {
            AppOutlinedButton(
                text = "Primary large",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Primary medium",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Secondary large",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Secondary medium",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Tertiary large",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Tertiary medium",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.MEDIUM
            ) {}
        }
    }
}

@Preview
@Composable
private fun PreviewInLightThemeDisabled() {
    AppTheme(isDarkTheme = false) {
        Column(
            modifier = Modifier
                .background(color = Color.White)
                .padding(16.dp)
        ) {
            AppOutlinedButton(
                text = "Primary large",
                enabled = false,
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Primary medium",
                enabled = false,
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Secondary large",
                enabled = false,
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Secondary medium",
                enabled = false,
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Tertiary large",
                enabled = false,
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Tertiary medium",
                enabled = false,
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.MEDIUM
            ) {}
        }
    }
}

@Preview
@Composable
private fun PreviewInLightThemeWithIconDisabled() {
    AppTheme(isDarkTheme = false) {
        Column(
            modifier = Modifier
                .background(color = Color.White)
                .padding(16.dp)
        ) {
            AppOutlinedButton(
                text = "Primary large",
                enabled = false,
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Primary medium",
                enabled = false,
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Secondary large",
                enabled = false,
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Secondary medium",
                enabled = false,
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Tertiary large",
                enabled = false,
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Tertiary medium",
                enabled = false,
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.MEDIUM
            ) {}
        }
    }
}

@Preview
@Composable
private fun PreviewInLightThemeWithBadge() {
    AppTheme(isDarkTheme = false) {
        Column(
            modifier = Modifier
                .background(color = Color.White)
                .padding(16.dp)
        ) {
            AppOutlinedButton(
                text = "Primary large",
                badge = "Soon",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Primary medium",
                badge = "Soon",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Secondary large",
                badge = "Soon",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Secondary medium",
                badge = "Soon",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Tertiary large",
                badge = "Soon",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppOutlinedButton(
                text = "Tertiary medium",
                badge = "Soon",
                  icon = {
                    Icon(
                        painter = painterResource(Res.drawable.ic_plus_24),
                        contentDescription = null,
                    )
                },
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.MEDIUM
            ) {}
        }
    }
}